import { useState, useEffect, useRef } from "react";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dumbbell, Plus, Trash2, Sparkles, RefreshCw, Save, Search, X, FileDown, Copy, Zap, BookmarkPlus, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import DayTabs from "@/components/training/DayTabs";
import ExerciseList from "@/components/training/ExerciseList";
import AddExerciseDialog from "@/components/training/AddExerciseDialog";
import TemplateDialog from "@/components/training/TemplateDialog";
import { PlanOverview } from "@/components/training/PlanOverview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingPlanPage({ onLogout }) {
  const { t } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [exerciseDB, setExerciseDB] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [crmClients, setCrmClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [currentPlan, setCurrentPlan] = useState(null);
  const [planName, setPlanName] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("hypertrophie");
  const [selectedMuscles, setSelectedMuscles] = useState([]);
  const [muscleListFilter, setMuscleListFilter] = useState("");
  const [days, setDays] = useState([{ name: "Tag 1", exercises: [], muscleGroups: [] }]);
  const [activeDay, setActiveDay] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [exerciseFilter, setExerciseFilter] = useState("");
  const [equipmentFilter, setEquipmentFilter] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [showOverview, setShowOverview] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const autoSaveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  useEffect(() => { loadData(); }, []);

  // Restore draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem("training_plan_draft");
    if (draft) {
      try {
        const d = JSON.parse(draft);
        if (d.planName && d.days?.length > 0 && d.days.some(day => day.exercises?.length > 0)) {
          setPlanName(d.planName);
          setDays(d.days);
          setSelectedCategory(d.selectedCategory || "hypertrophie");
          setSelectedMuscles(d.selectedMuscles || []);
          toast.info("Entwurf wiederhergestellt", { duration: 3000 });
        }
      } catch { /* invalid draft */ }
    }
  }, []);

  // Save draft to localStorage for unsaved plans
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (!currentPlan && planName.trim() && days.some(d => d.exercises?.length > 0)) {
      localStorage.setItem("training_plan_draft", JSON.stringify({ planName, days, selectedCategory, selectedMuscles }));
    }
  }, [days, planName, selectedCategory, selectedMuscles, currentPlan]);

  // Auto-save for existing plans
  useEffect(() => {
    if (isInitialLoad.current || !currentPlan || !planName.trim()) return;
    setAutoSaveStatus(null);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        const payload = { name: planName, client_id: selectedClient?.id || null, category: selectedCategory, muscle_groups: selectedMuscles, days, exercises: [] };
        await axios.put(`${API}/training-plans/${currentPlan.id}`, payload, { headers: getHeaders() });
        setAutoSaveStatus("saved");
        const plansRes = await axios.get(`${API}/training-plans`, { headers: getHeaders() });
        setPlans(plansRes.data.plans || []);
      } catch (err) { console.error("Auto-save failed:", err); setAutoSaveStatus(null); }
    }, 2000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [days, planName, selectedCategory, selectedMuscles, selectedClient]);

  // Reset initial load flag after plan changes
  useEffect(() => {
    if (isInitialLoad.current) {
      const timer = setTimeout(() => { isInitialLoad.current = false; }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPlan]);

  const loadData = async () => {
    try {
      const h = getHeaders();
      const [dbRes, plansRes, clientsRes] = await Promise.all([
        axios.get(`${API}/exercises/database`, { headers: h }),
        axios.get(`${API}/training-plans`, { headers: h }),
        axios.get(`${API}/crm/clients`, { headers: h }).catch(() => ({ data: { clients: [] } })),
      ]);
      setExerciseDB(dbRes.data.exercises);
      setMuscleGroups(dbRes.data.muscle_groups);
      setCategories(dbRes.data.categories);
      setPlans(plansRes.data.plans || []);
      const clients = clientsRes.data.clients || clientsRes.data || [];
      setCrmClients(Array.isArray(clients) ? clients : []);
    } catch { toast.error("Fehler beim Laden"); }
  };

  const currentExercises = days[activeDay]?.exercises || [];
  const setCurrentExercises = (fn) => {
    setDays(prev => prev.map((d, i) => i === activeDay ? { ...d, exercises: typeof fn === "function" ? fn(d.exercises) : fn } : d));
  };

  const toggleMuscle = (id) => {
    setSelectedMuscles(prev => {
      const next = prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id];
      // Save muscle groups to current day
      setDays(d => d.map((day, i) => i === activeDay ? { ...day, muscleGroups: next } : day));
      return next;
    });
  };

  // Sync selectedMuscles when switching days
  const handleSetActiveDay = (idx) => {
    setActiveDay(idx);
    setMuscleListFilter("");
    // Load muscle groups from target day, or auto-detect from exercises
    const day = days[idx];
    if (day?.muscleGroups?.length > 0) {
      setSelectedMuscles(day.muscleGroups);
    } else {
      // Auto-detect from exercises
      const muscles = [...new Set((day?.exercises || []).map(e => e.muscle_group).filter(Boolean))];
      setSelectedMuscles(muscles);
    }
  };

  // Auto-detected muscles from current day's exercises
  const dayMuscles = [...new Set(currentExercises.map(e => e.muscle_group).filter(Boolean))];

  // Filtered exercises for display (by muscle filter)
  const displayedExercises = muscleListFilter
    ? currentExercises.filter(e => e.muscle_group === muscleListFilter)
    : currentExercises;

  const handleGenerate = async () => {
    if (selectedMuscles.length === 0) { toast.error("Bitte Muskelgruppen auswählen"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/training-plans/generate`, {
        category: selectedCategory, muscle_groups: selectedMuscles, existing_exercises: currentExercises, max_exercises: 12,
      }, { headers: getHeaders() });
      setCurrentExercises(res.data.exercises);
      toast.success(`${res.data.exercises.length} Übungen generiert`);
    } catch { toast.error("Fehler bei der Generierung"); }
    setGenerating(false);
  };

  const handleRegenerateOne = async (idx) => {
    const ex = currentExercises[idx];
    setRegeneratingIdx(idx);
    try {
      const res = await axios.post(`${API}/training-plans/regenerate-one`, {
        category: selectedCategory, muscle_group: ex.muscle_group,
        exclude_exercise_ids: currentExercises.filter(e => e.muscle_group === ex.muscle_group).map(e => e.exercise_id),
      }, { headers: getHeaders() });
      setCurrentExercises(prev => prev.map((e, i) => i === idx ? res.data : e));
      toast.success("Übung ersetzt");
    } catch { toast.error("Keine Alternative verfügbar"); }
    setRegeneratingIdx(null);
  };

  const handleSave = async () => {
    if (!planName.trim()) { toast.error("Bitte Plan-Name eingeben"); return; }
    setSaving(true);
    try {
      const payload = { name: planName, client_id: selectedClient?.id || null, category: selectedCategory, muscle_groups: selectedMuscles, days, exercises: [] };
      if (currentPlan) {
        await axios.put(`${API}/training-plans/${currentPlan.id}`, payload, { headers: getHeaders() });
        toast.success("Plan aktualisiert");
      } else {
        const res = await axios.post(`${API}/training-plans`, payload, { headers: getHeaders() });
        setCurrentPlan(res.data);
        toast.success("Plan erstellt");
      }
      localStorage.removeItem("training_plan_draft");
      await loadData();
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  };

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/training-plans/${planId}`, { headers: getHeaders() });
      toast.success("Plan gelöscht"); loadData();
      if (currentPlan?.id === planId) resetForm();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const handleDuplicate = async (plan) => {
    try {
      const payload = {
        name: `${plan.name} (Kopie)`,
        client_id: null,
        category: plan.category,
        muscle_groups: plan.muscle_groups || [],
        days: (plan.days || []).map(d => ({ ...d })),
        exercises: [],
      };
      const res = await axios.post(`${API}/training-plans`, payload, { headers: getHeaders() });
      toast.success("Plan dupliziert — Kunde kann jetzt geändert werden");
      await loadData();
      loadPlan(res.data);
    } catch { toast.error("Fehler beim Duplizieren"); }
  };

  const handleSaveAsTemplate = async (planId) => {
    try {
      // Auto-save first
      if (currentPlan?.id === planId && planName.trim()) {
        const payload = { name: planName, client_id: selectedClient?.id || null, category: selectedCategory, muscle_groups: selectedMuscles, days, exercises: [] };
        await axios.put(`${API}/training-plans/${planId}`, payload, { headers: getHeaders() });
      }
      const res = await axios.post(`${API}/training-plans/${planId}/save-as-template`, {
        name: planName || "Meine Vorlage",
      }, { headers: getHeaders() });
      toast.success(`Vorlage "${res.data.name}" gespeichert`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler beim Speichern als Vorlage"); }
  };

  const handleExportPDF = async (planId) => {
    try {
      // Auto-save current state if editing this plan
      if (currentPlan?.id === planId && planName.trim()) {
        const payload = { name: planName, client_id: selectedClient?.id || null, category: selectedCategory, muscle_groups: selectedMuscles, days, exercises: [] };
        await axios.put(`${API}/training-plans/${planId}`, payload, { headers: getHeaders() });
      }
      const res = await axios.get(`${API}/training-plans/${planId}/pdf`, {
        headers: getHeaders(),
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Trainingsplan_${planName || "Plan"}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error(err?.response?.status === 404 ? "Plan zuerst speichern" : "PDF-Export fehlgeschlagen");
    }
  };

  const loadPlan = (plan) => {
    isInitialLoad.current = true;
    setCurrentPlan(plan); setPlanName(plan.name); setSelectedCategory(plan.category);
    setMuscleListFilter(""); setAutoSaveStatus(null);
    let loadedDays;
    if (plan.days?.length > 0) {
      loadedDays = plan.days.map(d => ({ ...d, muscleGroups: d.muscleGroups || [] }));
    } else if (plan.exercises?.length > 0) {
      loadedDays = [{ name: "Tag 1", exercises: plan.exercises, muscleGroups: [] }];
    } else {
      loadedDays = [{ name: "Tag 1", exercises: [], muscleGroups: [] }];
    }
    setDays(loadedDays);
    setActiveDay(0);
    const firstDayMuscles = loadedDays[0]?.muscleGroups?.length > 0
      ? loadedDays[0].muscleGroups
      : (plan.muscle_groups || []);
    setSelectedMuscles(firstDayMuscles);
    const client = crmClients.find(c => c.id === plan.client_id);
    setSelectedClient(client || null);
    setClientSearch(client ? `${client.vorname} ${client.nachname}` : "");
    localStorage.removeItem("training_plan_draft");
  };

  const resetForm = () => {
    isInitialLoad.current = true;
    setCurrentPlan(null); setPlanName(""); setSelectedClient(null); setClientSearch("");
    setSelectedCategory("hypertrophie"); setSelectedMuscles([]); setMuscleListFilter("");
    setDays([{ name: "Tag 1", exercises: [], muscleGroups: [] }]); setActiveDay(0);
    setAutoSaveStatus(null);
    localStorage.removeItem("training_plan_draft");
  };

  const addDay = () => {
    setDays(prev => {
      const nd = [...prev, { name: `Tag ${prev.length + 1}`, exercises: [], muscleGroups: [] }];
      setActiveDay(nd.length - 1);
      return nd;
    });
    setSelectedMuscles([]);
    setMuscleListFilter("");
  };
  const removeDay = (idx) => {
    if (days.length <= 1) return;
    setDays(prev => prev.filter((_, i) => i !== idx));
    setActiveDay(prev => Math.min(prev, days.length - 2));
  };
  const renameDayInline = (idx, newName) => setDays(prev => prev.map((d, i) => i === idx ? { ...d, name: newName } : d));

  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await axios.post(`${API}/training-categories`, { name: newCategoryName.trim() }, { headers: getHeaders() });
      setCategories(prev => [...prev, res.data]);
      setNewCategoryName(""); toast.success(`Kategorie "${res.data.name}" hinzugefügt`);
    } catch { toast.error("Fehler beim Hinzufügen"); }
  };
  const removeCategory = async (catId) => {
    try {
      await axios.delete(`${API}/training-categories/${catId}`, { headers: getHeaders() });
      setCategories(prev => prev.filter(c => c.id !== catId));
      if (selectedCategory === catId) setSelectedCategory("hypertrophie");
      toast.success("Kategorie gelöscht");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const removeExercise = (idx) => setCurrentExercises(prev => prev.filter((_, i) => i !== idx));
  const updateExercise = (idx, field, value) => setCurrentExercises(prev => prev.map((ex, i) => i === idx ? { ...ex, [field]: value } : ex));
  const [addToSection, setAddToSection] = useState("main");

  const addExerciseFromDB = (ex) => {
    setCurrentExercises(prev => [...prev, { exercise_id: ex.id, name: ex.name, muscle_group: ex.muscle, description: ex.description, image: ex.image, sets: "3", reps: "12", weight_kg: "0", section: addToSection }]);
    setShowAddExercise(false); toast.success(`${ex.name} → ${addToSection === "warmup" ? "Aufwärmen" : addToSection === "cooldown" ? "Dehnung" : "Haupttraining"}`);
  };

  // Auto-save plan when image or data changes (called from ExerciseList)
  const autoSavePlan = async (updatedDays) => {
    if (!currentPlan || !planName.trim()) return;
    try {
      const payload = { name: planName, client_id: selectedClient?.id || null, category: selectedCategory, muscle_groups: selectedMuscles, days: updatedDays || days, exercises: [] };
      await axios.put(`${API}/training-plans/${currentPlan.id}`, payload, { headers: getHeaders() });
    } catch (err) { console.error("Auto-save failed:", err); }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    setCurrentExercises(prev => { const items = [...prev]; const [moved] = items.splice(dragIdx, 1); items.splice(idx, 0, moved); return items; });
    setDragIdx(null); setDragOverIdx(null);
  };

  const filteredClients = crmClients.filter(c => `${c.vorname} ${c.nachname}`.toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredDBExercises = exerciseDB.filter(e => {
    const matchesText = !exerciseFilter || e.name.toLowerCase().includes(exerciseFilter.toLowerCase()) || e.muscle === exerciseFilter;
    const matchesEquipment = !equipmentFilter || (e.equipment || "").toLowerCase().includes(equipmentFilter);
    const notInPlan = !currentExercises.find(ex => ex.exercise_id === e.id);
    return matchesText && matchesEquipment && notInPlan;
  });
  const getMuscleLabel = (id) => muscleGroups.find(m => m.id === id)?.name || id;
  const totalExercises = days.reduce((sum, d) => sum + (d.exercises?.length || 0), 0);
  const defaultCatIds = ["hypertrophie","kraft","ausdauer","hyrox","ems","reha","pilates","yoga"];

  return (
    <Layout onLogout={onLogout}>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto flex flex-col" style={{ height: "calc(100vh - 64px)" }} data-testid="training-plan-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0 flex-wrap gap-2">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Dumbbell className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" /> KI-Trainingsplaner
          </h1>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowTemplates(true)} data-testid="templates-btn"><Zap className="w-3.5 h-3.5 sm:mr-1 text-amber-500" /><span className="hidden sm:inline"> Vorlagen</span></Button>
            {currentPlan && days.some(d => d.exercises?.length > 0) && (
              <Button size="sm" variant={showOverview ? "default" : "outline"} onClick={() => setShowOverview(!showOverview)} data-testid="overview-btn" className={showOverview ? "bg-slate-900 hover:bg-slate-800" : ""}>
                <Eye className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> {showOverview ? "Editor" : "Übersicht"}</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={resetForm} data-testid="new-plan-btn"><Plus className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Neuer Plan</span></Button>
          </div>
        </div>

        {/* Main grid: fill remaining height */}
        <div className="grid lg:grid-cols-[220px_1fr] gap-4 flex-1 min-h-0">
          {/* Saved Plans - scrollable sidebar */}
          <div className="overflow-y-auto space-y-2 pr-1">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0 bg-white pb-1 z-10">Gespeicherte Pläne</h2>
            {plans.length === 0 && <p className="text-sm text-slate-400">Noch keine Pläne erstellt</p>}
            {plans.map(plan => {
              const client = crmClients.find(c => c.id === plan.client_id);
              const dayCount = plan.days?.length || 1;
              const exCount = plan.days?.reduce((s, d) => s + (d.exercises?.length || 0), 0) || plan.exercises?.length || 0;
              return (
                <Card key={plan.id} className={`cursor-pointer hover:shadow-md transition-shadow ${currentPlan?.id === plan.id ? 'ring-2 ring-amber-400' : ''}`}
                  onClick={() => loadPlan(plan)} data-testid={`plan-card-${plan.id}`}>
                  <CardContent className="p-2.5">
                    <p className="text-xs font-semibold text-slate-900 truncate" title={plan.name}>{plan.name}</p>
                    {client && <p className="text-[10px] text-violet-600 truncate">{client.vorname} {client.nachname}</p>}
                    <p className="text-[10px] text-slate-400 mt-0.5">{dayCount} Tage • {exCount} Übungen</p>
                    <div className="flex items-center gap-0.5 mt-1.5 border-t border-slate-100 pt-1.5">
                      <button onClick={(e) => { e.stopPropagation(); handleSaveAsTemplate(plan.id); }} className="p-1 rounded hover:bg-amber-50" title="Als Vorlage speichern" data-testid={`save-template-${plan.id}`}><BookmarkPlus className="w-3 h-3 text-amber-500" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDuplicate(plan); }} className="p-1 rounded hover:bg-blue-50" title="Duplizieren" data-testid={`duplicate-plan-${plan.id}`}><Copy className="w-3 h-3 text-blue-500" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleExportPDF(plan.id); }} className="p-1 rounded hover:bg-slate-100" title="PDF" data-testid={`pdf-plan-${plan.id}`}><FileDown className="w-3 h-3 text-slate-500" /></button>
                      <div className="flex-1" />
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(plan.id); }} className="p-1 rounded hover:bg-red-50" data-testid={`delete-plan-${plan.id}`}><Trash2 className="w-3 h-3 text-red-400" /></button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Plan Editor or Overview */}
          {showOverview ? (
            <div className="overflow-y-auto pr-1">
              <PlanOverview days={days} setDays={setDays} getMuscleLabel={getMuscleLabel} onClose={() => setShowOverview(false)}
                onAddExercise={() => setShowAddExercise(true)} setActiveDay={handleSetActiveDay} />
            </div>
          ) : (
          <div className="flex flex-col min-h-0">
            {/* Config section (non-scrollable) */}
            <div className="space-y-4 flex-shrink-0 pb-3">
              {/* Client */}
              <div className="relative" data-testid="client-search">
                <label className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-1 block">Kunde aus CRM</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-violet-400" />
                  <Input placeholder="Kunden suchen..." value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    className="pl-9 border-violet-200 focus:border-violet-400 focus:ring-violet-200" data-testid="client-search-input" />
                  {selectedClient && <button onClick={() => { setSelectedClient(null); setClientSearch(""); }} className="absolute right-3 top-2.5"><X className="w-4 h-4 text-slate-400" /></button>}
                </div>
                {showClientDropdown && !selectedClient && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-violet-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredClients.length > 0 ? filteredClients.slice(0, 10).map(c => (
                      <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm"
                        onClick={() => { setSelectedClient(c); setClientSearch(`${c.vorname} ${c.nachname}`); setShowClientDropdown(false); }}>
                        {c.vorname} {c.nachname} <span className="text-xs text-slate-400">{c.email}</span>
                      </button>
                    )) : <p className="px-3 py-2 text-xs text-slate-400">Kein Kunde gefunden</p>}
                  </div>
                )}
              </div>

              {/* Name + Category */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-1 block">Plan-Name</label>
                  <Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder="z.B. Hypertrophie Plan A" data-testid="plan-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-1 block">Kategorie</label>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map(c => (
                      <div key={c.id} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border cursor-pointer transition-all ${
                        selectedCategory === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                      }`} onClick={() => setSelectedCategory(c.id)} data-testid={`cat-${c.id}`}>
                        {c.name}
                        {!defaultCatIds.includes(c.id) && <button onClick={(e) => { e.stopPropagation(); removeCategory(c.id); }} className="ml-0.5 hover:text-red-300"><X className="w-2.5 h-2.5" /></button>}
                      </div>
                    ))}
                    <div className="flex items-center gap-1">
                      <Input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Neue..." className="h-6 w-20 text-[11px]"
                        onKeyDown={e => e.key === "Enter" && addCategory()} data-testid="new-category-input" />
                      <button onClick={addCategory} className="h-6 w-6 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200" data-testid="add-category-btn"><Plus className="w-3 h-3 text-slate-600" /></button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Muscle groups - per day + filter */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  Muskelgruppen
                  {muscleListFilter && <span className="ml-2 text-amber-600 normal-case">(Filter: {muscleGroups.find(m => m.id === muscleListFilter)?.name})</span>}
                </label>
                <div className="flex flex-wrap gap-1.5" data-testid="muscle-group-selector">
                  {currentExercises.length > 0 && (
                    <button
                      onClick={() => setMuscleListFilter("")}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${!muscleListFilter ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'}`}
                      data-testid="muscle-filter-all"
                    >Alle ({currentExercises.length})</button>
                  )}
                  {muscleGroups.map(mg => {
                    const isSelected = selectedMuscles.includes(mg.id);
                    const inDay = dayMuscles.includes(mg.id);
                    const isFilter = muscleListFilter === mg.id;
                    const count = currentExercises.filter(e => e.muscle_group === mg.id).length;
                    return (
                      <button key={mg.id}
                        onClick={() => {
                          if (inDay) {
                            // Muscle is in the day → use as filter
                            setMuscleListFilter(prev => prev === mg.id ? "" : mg.id);
                          } else {
                            // Muscle not in day → toggle for AI generation
                            toggleMuscle(mg.id);
                          }
                        }}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                          isFilter ? 'bg-amber-500 text-white border-amber-500 ring-2 ring-amber-300'
                          : inDay ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:border-emerald-500'
                          : isSelected ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400'
                        }`}
                        data-testid={`muscle-${mg.id}`}
                      >
                        {mg.name}{count > 0 ? ` (${count})` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Day tabs */}
              <DayTabs days={days} activeDay={activeDay} setActiveDay={handleSetActiveDay} addDay={addDay} removeDay={removeDay} renameDayInline={renameDayInline} />

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={handleGenerate} disabled={generating || selectedMuscles.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-plan-btn">
                  {generating ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  {generating ? "Generiere..." : `KI für ${days[activeDay]?.name || "Tag"}`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddExercise(true)} data-testid="add-exercise-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Übung</Button>
                {currentPlan && <Button size="sm" variant="outline" onClick={() => handleExportPDF(currentPlan.id)} data-testid="export-pdf-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>}
                <span className="text-xs text-slate-400 ml-auto">
                  {muscleListFilter ? `${displayedExercises.length}/${currentExercises.length}` : currentExercises.length} Übungen • Gesamt: {totalExercises}
                </span>
              </div>
            </div>

            {/* Exercises area - SCROLLABLE, takes remaining height */}
            <div className="flex-1 overflow-y-auto min-h-0 pb-4" data-testid="exercises-scroll-area">
              {currentExercises.length > 0 ? (
                <>
                  <ExerciseList
                    exercises={displayedExercises} activeDay={activeDay} getMuscleLabel={getMuscleLabel}
                    onRegenerate={handleRegenerateOne} regeneratingIdx={regeneratingIdx}
                    onRemove={removeExercise} onUpdate={updateExercise}
                    expandedExercise={expandedExercise} setExpandedExercise={setExpandedExercise}
                    dragIdx={dragIdx} dragOverIdx={dragOverIdx}
                    onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    onAutoSavePlan={autoSavePlan} days={days}
                  />
                </>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <Dumbbell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Wählen Sie Muskelgruppen und klicken Sie "KI für {days[activeDay]?.name}"</p>
                  <p className="text-xs text-slate-400 mt-1">oder fügen Sie Übungen manuell hinzu</p>
                </div>
              )}

              {/* Save bar - always visible */}
              <div className="flex items-center justify-end gap-3 pt-4 mt-2 border-t border-slate-100">
                {autoSaveStatus === "saving" && (
                  <span className="flex items-center gap-1 text-[10px] text-slate-400"><RefreshCw className="w-3 h-3 animate-spin" /> Speichern...</span>
                )}
                {autoSaveStatus === "saved" && (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-500" data-testid="auto-save-status"><Save className="w-3 h-3" /> Gespeichert</span>
                )}
                {currentPlan && <Button size="sm" variant="outline" onClick={() => handleExportPDF(currentPlan.id)} data-testid="export-pdf-bottom"><FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF herunterladen</Button>}
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 px-6" data-testid="save-plan-btn">
                  {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  {currentPlan ? "Aktualisieren" : "Speichern"}
                </Button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      <AddExerciseDialog
        open={showAddExercise} onOpenChange={setShowAddExercise}
        exerciseFilter={exerciseFilter} setExerciseFilter={setExerciseFilter}
        equipmentFilter={equipmentFilter} setEquipmentFilter={setEquipmentFilter}
        muscleGroups={muscleGroups} filteredExercises={filteredDBExercises}
        getMuscleLabel={getMuscleLabel} onAddExercise={addExerciseFromDB}
        onRefreshDB={loadData}
        addToSection={addToSection} setAddToSection={setAddToSection}
      />

      <TemplateDialog
        open={showTemplates} onOpenChange={setShowTemplates}
        onPlanCreated={(plan) => { loadData(); loadPlan(plan); }}
      />
    </Layout>
  );
}
